import {
  useApi, useAsyncData, useI18n, useNuxtApp, useRuntimeConfig
} from '#imports'
import {
  STORES_BY_FOREIGN_COUNTRY_QUERY,
  STORES_BY_PROVINCE_LIGHT_QUERY
} from '~/graphql/stores/'
import { COUNTRIES, PROVINCES } from '~/graphql/stores/store-locator'
import { getStoreSearchDisplayTitle } from '~/utils/stores'

const NOT_IN_LIST_VALUE = 'not-in-list'

/**
 * Composable for country → province → store cascading selection.
 *
 * Used by WarrantyOkForm.vue, Block/AppointmentCountry.vue, and Form/Builder.vue (store-locator forms).
 *
 * @param {string|number} italyId - The value used to identify the Italy option in the country
 *   select. Defaults to the legacy string 'italy' for backward compat with WarrantyOkForm and
 *   LandingForm. Form/Builder.vue passes the Craft entry ID (e.g. '205887') because dealerCountry
 *   is a Field_Entries field whose mutation type is [Int], not a plain string.
 *
 * @param {boolean} excludeItaly - When true, the Italy option is omitted from countryOptions
 *   entirely. Used by Form/Builder.vue when rendered in an "estero" (abroad) context via the
 *   storeLocatorExcludeItaly prop. The COUNTRIES query still fetches Italy (status: null) so the
 *   data is available, but the computed list simply does not include it.
 *
 * Important: this composable is NOT imported unconditionally in Builder.vue.
 * It is only instantiated when the rendered form actually contains store-locator handles
 * (dealerCountry / dealerProvince / dealerStore), to avoid spurious GraphQL calls on every form.
 */
export const useStoreSelection = async ({ italyId = 'italy', excludeItaly = false } = {}) => {
  const { t, locale } = useI18n()
  const italyEntryId = useRuntimeConfig().public.ITALY_ENTRY_ID

  // Store selection state
  const stores = ref([])
  const storesLoading = ref(false)
  const selectedCountry = ref('')
  const selectedProvince = ref('')
  const { bus } = useNuxtApp()

  // Call both useAsyncData synchronously (before any await) so they register
  // in the Nuxt instance context. Awaiting sequentially loses the instance
  // after the first await, causing "composable called outside setup" errors
  // when the component is rendered dynamically (e.g. via BlocksLoader).
  const foreignCountriesPromise = useAsyncData(
    `forms-countries-${locale.value}`,
    // status: null fetches entries of all statuses so that forms still work even if a country
    // is accidentally disabled in the CMS. Italy is filtered out of the abroad store locator
    // page on the frontend (via ITALY_ENTRY_ID) rather than by CMS status.
    () => useApi(COUNTRIES, { countriesLabel: true, status: null }),
    { transform: r => r?.types || [] }
  )

  const provincesPromise = useAsyncData(
    `forms-provinces-${locale.value}`,
    () => useApi(PROVINCES),
    { transform: r => r?.items || [] }
  )

  const [ { data: foreignCountries }, { data: provinces } ] = await Promise.all([ foreignCountriesPromise, provincesPromise ])

  // Country options: Italy first + all foreign nations (Italy entry excluded to avoid duplicates).
  // When excludeItaly is true (abroad context), Italy is omitted completely from the list.
  const countryOptions = computed(() => {
    const otherCountries = (foreignCountries.value ?? [])
      // Country labels come straight from the Craft entry title, which is translated per site
      // (titleTranslationMethod: "site" on the storeLocatorCountries section).
      .map(c => ({ label: c.title || '', value: c.id }))
      // Always filter out the Italy entry from the API results to prevent duplicates, since Italy
      // is explicitly prepended as the first option (unless excludeItaly is true). WarrantyOkForm
      // and LandingForm use the legacy italyId 'italy', which never matches the numeric entry ID,
      // so the entry must also be filtered by its actual ID (ITALY_ENTRY_ID).
      .filter(d => d.label && String(d.value) !== String(italyId) && String(d.value) !== String(italyEntryId))
      .sort((a, b) => a.label.localeCompare(b.label))

    if (excludeItaly) return otherCountries
    return [ { value: italyId, label: t('storeLocator.page.titleItaly') }, ...otherCountries ]
  })

  // Province options (Italy only)
  const provinceOptions = computed(() =>
    provinces.value.map(p => ({
      label: p.title,
      value: p.slug
    })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  )

  /**
   * Store options with value = entry ID (integer as string).
   *
   * NOTE: Builder.vue intentionally does NOT use this for its store dropdown.
   * Instead it builds a custom options list (from stores.value directly) where value is the
   * human-readable store name + address, so that is what gets saved in the Formie submission.
   * This storeOptions computed is kept for LandingForm.vue which needs the ID to look up the
   * full store entry after selection.
   */
  const storeOptions = computed(() => {
    const notInList = { value: NOT_IN_LIST_VALUE, label: t('forms.labels.storeNotInList') }
    const options = stores.value
      .map(s => ({
        value: s.id,
        label: getStoreSearchDisplayTitle(s) || `Store ${s.id}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    return [ notInList, ...options ]
  })

  // Conditional visibility
  const showProvinceSelect = computed(() => String(selectedCountry.value) === String(italyId))

  const showStoreSelect = computed(() =>
    (String(selectedCountry.value) === String(italyId) && selectedProvince.value)
    || (selectedCountry.value && String(selectedCountry.value) !== String(italyId))
  )

  const getCoordinates = location => location?.coordinates.split(',') ?? []

  const getStoresByProvince = async province => {
    const _results = await useApi(STORES_BY_PROVINCE_LIGHT_QUERY, {
      province: province
    })
    return _results?.entries
  }

  const getStoresByForeignCountry = async country => {
    const _stores = await useApi(STORES_BY_FOREIGN_COUNTRY_QUERY, {
      foreignCountry: country
    })

    return _stores.entries?.map(s => {
      const [ lat, lng ] = getCoordinates(s.location)

      return {
        ...s,
        lat,
        lng
      }
    })
  }

  // Handle country change
  const handleCountryChange = async country => {
    selectedCountry.value = country
    selectedProvince.value = ''
    stores.value = []

    // If foreign country selected, fetch stores immediately
    if (country && String(country) !== String(italyId)) {
      storesLoading.value = true
      try {
        stores.value = await getStoresByForeignCountry(country)
      } catch (err) {
        console.error('Error fetching foreign stores:', err)
        stores.value = []
      } finally {
        storesLoading.value = false
        bus.emit('scroll:refresh')
      }
    }
  }

  // Handle province change (Italy only)
  const handleProvinceChange = async province => {
    selectedProvince.value = province
    stores.value = []

    if (province) {
      storesLoading.value = true
      try {
        stores.value = await getStoresByProvince(province)
      } catch (err) {
        console.error('Error fetching Italian stores:', err)
        stores.value = []
      } finally {
        storesLoading.value = false
        bus.emit('scroll:refresh')
      }
    }
  }

  return {
    stores,
    selectedCountry,
    selectedProvince,
    storesLoading,
    countryOptions,
    provinceOptions,
    storeOptions,
    showProvinceSelect,
    showStoreSelect,
    handleCountryChange,
    handleProvinceChange,
    NOT_IN_LIST_VALUE
  }
}
